import { branch, compose, withHandlers } from 'recompose';
import { chunk, isEmpty } from 'lodash';

import { isEditMode, isEditingExistingBarber } from '../helpers/isEditMode';
import { showNotification } from '../../../../../common/helpers/notifications';
import { getLocationsUpdate, getNewBarberValues, getUpdatedBarberValues } from '../helpers/submitPayloadHelpers';
import { getValidationWarnings, validateBeforeSubmit } from '../helpers/submitValidationHelpers';
import { TAB_NAMES } from './withTabs';
import { LOCATIONS_ALL_ID } from '../constants';
import withBatchUpdateStatus from './withBatchUpdateStatus';
import { Logger } from '../../../../../../modules/infra/observability/logger';

const LOCATIONS_UPDATE_BATCH_SIZE = 1;
const UPDATE_LOCATIONS_IN_BATCHES_LIMIT = 5;

const getInitialLocationsBatchUpdateStatus = (props, patchUpdate) => patchUpdate.map(shopUpdate => ({
  shopId: shopUpdate.shopId,
  shopName: props.shopNamesById[shopUpdate.shopId],
  status: 'waiting',
}));

const getUpdatedBatchUpdateStatus = (batchUpdateStatus, updatedBatch, isSuccess) => batchUpdateStatus.map(
  shopUpdateStatus => (
    updatedBatch.some(shopUpdate => shopUpdate.shopId === shopUpdateStatus.shopId)
      ? { ...shopUpdateStatus, status: isSuccess ? 'success' : 'error' }
      : shopUpdateStatus
  )
);

const saveBarberLocationsInOneGo = async (props, patchUpdate) => {
  const barberId = props.brandBarberId;

  if (barberId) {
    return props.modifyBarber(barberId, patchUpdate);
  }

  return props.saveBarber(patchUpdate);
};

const saveBarberLocationsInBatches = async (props, patchUpdate) => {
  const batchPatchUpdates = chunk(patchUpdate, LOCATIONS_UPDATE_BATCH_SIZE);

  let batchUpdateStatus = getInitialLocationsBatchUpdateStatus(props, patchUpdate);
  props.setBatchUpdateStatus(batchUpdateStatus);
  props.setBatchUpdateInProgress(true);

  let barberId = props.brandBarberId;
  let savedBarber = null;

  // for-of allows sequential promise execution
  // https://stackoverflow.com/questions/37576685/using-async-await-with-a-foreach-loop
  // eslint-disable-next-line no-restricted-syntax
  for (const batchPatchUpdate of batchPatchUpdates) {
    let isSuccess;
    try {
      if (!barberId) {
        // eslint-disable-next-line no-await-in-loop
        savedBarber = await props.saveBarber(batchPatchUpdate);
        barberId = savedBarber.id;
      } else {
        // eslint-disable-next-line no-await-in-loop
        savedBarber = await props.modifyBarber(barberId, batchPatchUpdate);
      }
      isSuccess = true;
    } catch (error) {
      isSuccess = false;
      Logger.error('Partial save of barber locations', {
        error,
        action: 'Partial save of barber locations',
      });
    }

    batchUpdateStatus = getUpdatedBatchUpdateStatus(batchUpdateStatus, batchPatchUpdate, isSuccess);
    props.setBatchUpdateStatus(batchUpdateStatus);
  }

  props.setBatchUpdateStatus([]);
  props.setBatchUpdateInProgress(false);

  return savedBarber;
};

const saveBarberLocations = async (props, patchUpdate) => {
  if (!patchUpdate.length) {
    return null;
  }

  if (patchUpdate.length <= UPDATE_LOCATIONS_IN_BATCHES_LIMIT) {
    return saveBarberLocationsInOneGo(props, patchUpdate);
  }

  return saveBarberLocationsInBatches(props, patchUpdate);
};

const saveExistingBarber = async (props, formData) => {
  const isLocationsTab = props.tabs[props.activeTab].title === TAB_NAMES.LOCATIONS;

  if (isLocationsTab) {
    const patchUpdate = getLocationsUpdate(props.selectedLocations, formData, props.brandShops);
    await saveBarberLocations(props, patchUpdate);
  } else {
    const patchUpdate = getUpdatedBarberValues(formData, props.initialValues, props.brandShops);
    if (patchUpdate.length > 0) {
      await props.modifyBarber(props.brandBarberId, patchUpdate);
    }
  }

  const isProfileTab = props.tabs[props.activeTab].title === TAB_NAMES.PROFILE;
  if (isProfileTab) {
    const avatar = formData[props.selectedLocation].avatar;
    if (avatar && avatar instanceof File) {
      await props.saveBarberImage(props.brandBarberId, avatar);
    }
  }
};

const saveNewBarber = async (props, formData) => {
  const patchCreate = getNewBarberValues(props.selectedLocations, formData, props.brandShops);
  const createdBarber = await saveBarberLocations(props, patchCreate);

  const avatar = formData[LOCATIONS_ALL_ID].avatar;
  if (avatar) {
    await props.saveBarberImage(createdBarber.id, avatar);
  }
};

const withAddOnSubmit = withHandlers({
  onSubmit: props => async () => {
    const formData = props.formValues;

    const isValid = await validateBeforeSubmit(props, formData);
    if (!isValid) {
      return;
    }

    const isLocationsTab = props.tabs[props.activeTab].title === TAB_NAMES.LOCATIONS;

    const validationWarnings = getValidationWarnings(props, formData);
    const hasWarnings = !isEmpty(validationWarnings);

    if (isEditingExistingBarber(props)) {
      await saveExistingBarber(props, formData);
    } else if (isLocationsTab) {
      await saveNewBarber(props, formData);
    }

    if (props.activeTab < props.tabs.length - 1) {
      props.setActiveTab(props.activeTab + 1);
    } else {
      showNotification({
        title: 'Success',
        level: 'success',
        message: hasWarnings
          ? `Barber created with following warnings:\n${validationWarnings}`
          : 'Barber created successfully',
      });

      props.resetBarber();
      props.router.push('/brand/barbers');
    }
  },
});

const withEditOnSubmit = withHandlers({
  onSubmit: props => async () => {
    const formData = props.formValues;

    const isValid = await validateBeforeSubmit(props, formData);
    if (!isValid) {
      return;
    }

    const validationWarnings = getValidationWarnings(props, formData);
    const hasWarnings = !isEmpty(validationWarnings);

    await saveExistingBarber(props, formData);

    showNotification({
      title: 'Success',
      level: 'success',
      message: hasWarnings
        ? `Barber updated with following warnings:\n${validationWarnings}`
        : 'Barber updated successfully',
    });
  },
});

const withOnSubmit = branch(props => isEditMode(props), withEditOnSubmit, withAddOnSubmit);

export default compose(
  withBatchUpdateStatus,
  withOnSubmit,
);
